/**
 * Notification Sound Utility
 * Plays audio alerts for notifications
 */

import Logger from './logger';

// Audio instances (preload for better performance)
let notificationSound = null;
let errorSound = null;

/**
 * Initialize audio files (preload)
 */
export function initializeNotificationSounds() {
  try {
    notificationSound = new Audio('/sounds/notification.mp3');
    errorSound = new Audio('/sounds/error.mp3');

    // Preload audio files
    notificationSound.load();
    errorSound.load();

    Logger.debug('Notification sounds initialized');
  } catch (error) {
    Logger.error('Failed to initialize notification sounds', error);
  }
}

/**
 * Play notification sound based on severity
 * @param {string} severity - Notification severity (info, success, warning, error, critical)
 * @param {number} volume - Volume level (0.0 to 1.0)
 */
export function playNotificationSound(severity = 'info', volume = 0.6) {
  try {
    // Initialize sounds if not already done
    if (!notificationSound || !errorSound) {
      initializeNotificationSounds();
    }

    let audio;
    if (severity === 'error' || severity === 'critical') {
      audio = errorSound;
    } else {
      audio = notificationSound;
    }

    if (!audio) {
      Logger.warn('Notification sound not available');
      return;
    }

    // Clone audio to allow multiple simultaneous plays
    const sound = audio.cloneNode();
    sound.volume = Math.max(0, Math.min(1, volume));

    // Play sound
    const playPromise = sound.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          Logger.debug('Notification sound played', { severity, volume });
        })
        .catch(error => {
          // Handle autoplay restrictions
          if (error.name === 'NotAllowedError') {
            Logger.warn('Sound playback blocked by browser autoplay policy');
          } else {
            Logger.error('Failed to play notification sound', error);
          }
        });
    }
  } catch (error) {
    Logger.error('Error playing notification sound', error);
  }
}

/**
 * Test notification sound playback
 * @param {string} severity - Notification severity
 * @param {number} volume - Volume level
 */
export function testNotificationSound(severity = 'info', volume = 0.6) {
  playNotificationSound(severity, volume);
}
